/**
 * MediaPicker - a focused image picker over the host site's media library.
 * Browse, search, upload, pick one. A light version of the web_admin one
 * (without folders, stock or video) - used for the invoice logo and for the
 * letter builder's image blocks. Rendered into document.body through a portal.
 * The chrome matches web_admin: zoom, a grid/list toggle, and a hovering tile
 * card (object-cover, not object-contain).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Upload, X, Search, Loader2, ImagePlus, LayoutGrid, List as ListIcon,
} from "lucide-react";
import { Button, Input, cn } from "@wa/ui";
import { useT } from "../i18n/I18nProvider";
import { listImages, uploadImage, type MediaAsset } from "../lib/media";
import { GridZoomControl, gridZoomStyle, readStoredZoom } from "./GridZoomControl";

interface Props {
  open: boolean;
  siteId: string;
  onClose: () => void;
  onPick: (asset: MediaAsset) => void;
}

function formatBytes(b: number): string {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export function MediaPicker({ open, siteId, onClose, onPick }: Props) {
  const t = useT();
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // View mode (grid / list) - kept in localStorage.
  const [view, setView] = useState<"grid" | "list">(() =>
    (typeof window !== "undefined" && window.localStorage.getItem("crm.mediaPicker.view") === "list") ? "list" : "grid",
  );
  useEffect(() => { window.localStorage.setItem("crm.mediaPicker.view", view); }, [view]);

  // Zoom (tile size in px) - kept in localStorage.
  const [tile, setTile] = useState<number>(() => readStoredZoom("crm.mediaPicker.tile", 140));
  useEffect(() => {
    const id = setTimeout(() => window.localStorage.setItem("crm.mediaPicker.tile", String(tile)), 200);
    return () => clearTimeout(id);
  }, [tile]);

  const fetchItems = useCallback(async (term: string) => {
    setLoading(true);
    try {
      const res = await listImages(siteId, { search: term || undefined, limit: 60 });
      setItems(res.items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => fetchItems(search), search ? 300 : 0);
    return () => clearTimeout(id);
  }, [open, search, fetchItems]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function handleFile(file: File) {
    setUploading(true);
    try {
      const asset = await uploadImage(siteId, file);
      onPick(asset);
      onClose();
    } finally {
      setUploading(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-[101] flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-elevated">
        <div className="flex items-center gap-3 border-b border-border p-4">
          <ImagePlus className="size-5 text-primary" />
          <h3 className="flex-1 font-semibold">{t("mediaPicker.title")}</h3>
          <button onClick={onClose} className="inline-flex size-8 items-center justify-center rounded-md hover:bg-accent" aria-label={t("mediaPicker.close")}>
            <X className="size-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-border bg-muted/40 p-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t("mediaPicker.search")} value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9" />
          </div>
          <div className="flex-1" />
          {view === "grid" && <GridZoomControl value={tile} onChange={setTile} slider />}
          {/* Vaate-lüliti - ruudustik / nimekiri (paremal üleval) */}
          <div className="inline-flex h-9 shrink-0 overflow-hidden rounded-md border">
            <button
              type="button"
              onClick={() => setView("grid")}
              title={t("media.viewGrid")}
              aria-label={t("media.viewGrid")}
              className={cn("inline-flex size-9 items-center justify-center transition-colors",
                view === "grid" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50")}
            >
              <LayoutGrid className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              title={t("media.viewList")}
              aria-label={t("media.viewList")}
              className={cn("inline-flex size-9 items-center justify-center border-l transition-colors",
                view === "list" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50")}
            >
              <ListIcon className="size-4" />
            </button>
          </div>
          <input ref={fileInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); }} />
          <Button onClick={() => fileInput.current?.click()} disabled={uploading} className="h-9">
            {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />} {t("mediaPicker.upload")}
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground"><Loader2 className="size-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <p className="font-semibold">{t("mediaPicker.empty")}</p>
              <p className="mt-1 text-xs">{t("mediaPicker.emptyHint")}</p>
            </div>
          ) : view === "list" ? (
            <div className="flex flex-col gap-1">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { onPick(m); onClose(); }}
                  className="group flex items-center gap-3 rounded-md border border-transparent px-2 py-1.5 text-left transition-colors hover:bg-accent"
                >
                  <div className="size-11 shrink-0 overflow-hidden rounded bg-muted">
                    <img src={m.url} alt={m.originalFilename} loading="lazy" className="size-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{m.originalFilename}</div>
                    {m.sizeBytes > 0 && <div className="text-[11px] tabular-nums text-muted-foreground">{formatBytes(m.sizeBytes)}</div>}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="grid gap-3" style={gridZoomStyle(tile)}>
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { onPick(m); onClose(); }}
                  title={m.originalFilename}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-primary hover:shadow-elevated"
                >
                  <img src={m.url} alt={m.originalFilename} loading="lazy" className="size-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="truncate text-[10px] font-medium text-white">{m.originalFilename}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
